import { useEffect, useState } from "react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, ClipboardCheck, BookOpen, Plus, X as XIcon } from "lucide-react";

const SERVICE_OPTIONS = [
  { id: "editing", label: "Editing" },
  { id: "cover_design", label: "Cover design" },
  { id: "typesetting", label: "Typesetting" },
  { id: "marketing", label: "Marketing" },
  { id: "distribution", label: "Distribution" },
  { id: "isbn", label: "ISBN" },
  { id: "printing", label: "Printing" },
  { id: "translation", label: "Translation" },
  { id: "audiobook", label: "Audiobook" },
];

const STATUS_PILL = {
  pending: { bg: "#FEF3C7", fg: "#92400E", label: "Pending admin review" },
  approved: { bg: "#DCFCE7", fg: "#166534", label: "Approved & live" },
  rejected: { bg: "#FEE2E2", fg: "#991B1B", label: "Rejected" },
  changes_requested: { bg: "#E0E7FF", fg: "#3730A3", label: "Changes requested" },
};

function StatusBanner({ profile }) {
  const meta = STATUS_PILL[profile?.approval_status] || STATUS_PILL.pending;
  return (
    <div
      className="p-4 mb-6 rounded-lg flex items-start gap-3"
      style={{ background: meta.bg, color: meta.fg }}
      data-testid="publisher-status-banner"
    >
      <ClipboardCheck className="w-5 h-5 mt-0.5" />
      <div>
        <p className="text-sm font-medium">{meta.label}</p>
        {profile?.review_message && (
          <p className="text-xs mt-1 opacity-90">Admin note: {profile.review_message}</p>
        )}
      </div>
    </div>
  );
}

function ProfileForm({ profile, onSaved }) {
  const [form, setForm] = useState(profile);
  const [saving, setSaving] = useState(false);

  const toggleService = (id) => {
    const next = new Set(form.services || []);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setForm({ ...form, services: [...next] });
  };

  const updateRate = (idx, key, val) => {
    const next = [...(form.per_service_rates || [])];
    next[idx] = { ...next[idx], [key]: val };
    setForm({ ...form, per_service_rates: next });
  };
  const addRate = () => setForm({ ...form, per_service_rates: [...(form.per_service_rates || []), { service: "", rate: "" }] });
  const removeRate = (idx) => setForm({ ...form, per_service_rates: (form.per_service_rates || []).filter((_, i) => i !== idx) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/publisher/profile", form);
      onSaved(data);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-6 max-w-3xl" data-testid="publisher-profile-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="mb-1.5 block">Company / Imprint name *</Label>
          <Input required value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="pub-form-company" />
        </div>
        <div>
          <Label className="mb-1.5 block">Tagline (one line)</Label>
          <Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Indie-friendly literary publisher" data-testid="pub-form-tagline" />
        </div>
      </div>

      <div>
        <Label className="mb-1.5 block">Logo URL</Label>
        <Input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" data-testid="pub-form-logo-url" />
      </div>

      <div>
        <Label className="mb-1.5 block">About / Description</Label>
        <Textarea rows={4} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pub-form-description" />
      </div>

      <div>
        <Label className="mb-2 block">Services offered</Label>
        <div className="flex flex-wrap gap-2" data-testid="pub-form-services">
          {SERVICE_OPTIONS.map((s) => {
            const active = (form.services || []).includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => toggleService(s.id)}
                className="px-3 py-1.5 rounded-full text-sm transition-colors"
                style={{
                  background: active ? "var(--bf-primary)" : "white",
                  color: active ? "white" : "var(--bf-text)",
                  border: `1px solid ${active ? "var(--bf-primary)" : "var(--bf-border)"}`,
                }}
                data-testid={`pub-form-service-${s.id}`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label className="mb-1.5 block">Base fee (per book)</Label>
          <Input type="number" value={form.base_fee ?? ""} onChange={(e) => setForm({ ...form, base_fee: e.target.value })} data-testid="pub-form-base-fee" />
        </div>
        <div>
          <Label className="mb-1.5 block">Currency</Label>
          <Input value={form.currency || "INR"} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} data-testid="pub-form-currency" />
        </div>
        <div>
          <Label className="mb-1.5 block">Turnaround</Label>
          <Input value={form.turnaround || ""} onChange={(e) => setForm({ ...form, turnaround: e.target.value })} placeholder="6-10 weeks" data-testid="pub-form-turnaround" />
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Per-service rates</Label>
        <div className="space-y-2" data-testid="pub-form-rates">
          {(form.per_service_rates || []).map((r, i) => (
            <div key={`rate-${i}-${r.service || ""}`} className="grid grid-cols-[1fr_140px_auto] gap-2">
              <Input placeholder="Service (e.g. Cover design)" value={r.service || ""} onChange={(e) => updateRate(i, "service", e.target.value)} data-testid={`pub-form-rate-svc-${i}`} />
              <Input placeholder="Rate" type="number" value={r.rate ?? ""} onChange={(e) => updateRate(i, "rate", e.target.value)} data-testid={`pub-form-rate-val-${i}`} />
              <Button type="button" variant="outline" size="sm" onClick={() => removeRate(i)} data-testid={`pub-form-rate-remove-${i}`}><XIcon className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addRate} data-testid="pub-form-rate-add"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add rate row</Button>
      </div>

      <div className="grid sm:grid-cols-[2fr_1fr] gap-4">
        <div>
          <Label className="mb-1.5 block">Current offers / discount note</Label>
          <Input value={form.discount_note || ""} onChange={(e) => setForm({ ...form, discount_note: e.target.value })} placeholder="First-time authors: 15% off" data-testid="pub-form-discount-note" />
        </div>
        <div>
          <Label className="mb-1.5 block">Discount %</Label>
          <Input type="number" min="0" max="100" value={form.discount_percent ?? ""} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} data-testid="pub-form-discount-percent" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="mb-1.5 block">Genres accepted (comma-separated)</Label>
          <Input value={(form.genres_accepted || []).join(", ")} onChange={(e) => setForm({ ...form, genres_accepted: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Fiction, Memoir, Poetry" data-testid="pub-form-genres" />
        </div>
        <div>
          <Label className="mb-1.5 block">Languages accepted (codes, comma-separated)</Label>
          <Input value={(form.languages_accepted || []).join(", ")} onChange={(e) => setForm({ ...form, languages_accepted: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="en, hi, ta" data-testid="pub-form-languages" />
        </div>
      </div>

      <div>
        <Label className="mb-1.5 block">Terms &amp; Conditions</Label>
        <Textarea rows={6} value={form.terms_and_conditions || ""} onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })} placeholder="Royalty splits, rights, refund policy…" data-testid="pub-form-terms" />
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label className="mb-1.5 block">Contact email</Label>
          <Input type="email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="pub-form-email" />
        </div>
        <div>
          <Label className="mb-1.5 block">Contact phone</Label>
          <Input value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="pub-form-phone" />
        </div>
        <div>
          <Label className="mb-1.5 block">Website</Label>
          <Input value={form.contact_website || ""} onChange={(e) => setForm({ ...form, contact_website: e.target.value })} data-testid="pub-form-website" />
        </div>
      </div>

      <div>
        <Label className="mb-1.5 block">Listing status</Label>
        <select
          className="border rounded px-3 py-2 text-sm"
          value={form.listing_status || "active"}
          onChange={(e) => setForm({ ...form, listing_status: e.target.value })}
          data-testid="pub-form-listing-status"
        >
          <option value="active">Active — accepting new books</option>
          <option value="paused">Paused — temporarily not accepting</option>
          <option value="not_accepting">Not accepting new books</option>
        </select>
      </div>

      <div className="pt-4 border-t" style={{ borderColor: "var(--bf-border)" }}>
        <Button type="submit" disabled={saving} className="text-white" style={{ background: "var(--bf-primary)" }} data-testid="pub-form-save">
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </form>
  );
}

function ProposalsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/publisher/proposals"); setRows(data); }
      catch { toast.error("Failed to load proposals"); }
      finally { setLoading(false); }
    })();
  }, []);

  const respond = async (id, status, response = "") => {
    try {
      const { data } = await api.patch(`/proposals/${id}`, { status, response });
      setRows((r) => r.map((p) => (p.proposal_id === id ? { ...p, ...data } : p)));
      toast.success(`Proposal ${status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>Loading…</p>;
  if (!rows.length) {
    return (
      <div className="p-10 text-center rounded-lg" style={{ background: "var(--bf-surface)", border: "1px dashed var(--bf-border)" }} data-testid="publisher-proposals-empty">
        <ClipboardCheck className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--bf-accent)" }} />
        <h3 className="font-serif text-xl mb-1">No proposals yet</h3>
        <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>When an author sends their book to you, it&apos;ll show up here.</p>
      </div>
    );
  }

  return (
    <ul className="space-y-3" data-testid="publisher-proposals-list">
      {rows.map((p) => (
        <li key={p.proposal_id} className="p-5 rounded-lg" style={{ background: "white", border: "1px solid var(--bf-border)" }} data-testid={`proposal-${p.proposal_id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="font-serif text-xl mb-1">{p.book?.title || "Untitled"}</p>
              <p className="text-xs mb-2" style={{ color: "var(--bf-text-muted)" }}>
                From <strong>{p.author?.name}</strong> · {p.author?.email}
              </p>
              {p.message && <p className="text-sm mb-2">{p.message}</p>}
              <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                {(p.book?.genres || []).join(", ")} · {p.book?.language?.toUpperCase()}
              </p>
              {p.publisher_response && (
                <p className="text-xs mt-2 pt-2 border-t" style={{ borderColor: "var(--bf-border)", color: "var(--bf-text-muted)" }}>
                  Your response: {p.publisher_response}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 items-end flex-shrink-0">
              <span className="text-xs px-2 py-1 rounded-full" style={{ background: "var(--bf-surface)" }}>{p.status}</span>
              {p.status === "pending" && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => respond(p.proposal_id, "rejected")} data-testid={`reject-${p.proposal_id}`}>Reject</Button>
                  <Button size="sm" className="text-white" style={{ background: "var(--bf-primary)" }} onClick={() => respond(p.proposal_id, "accepted")} data-testid={`accept-${p.proposal_id}`}>Accept</Button>
                </div>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function BrowseBooks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/publisher/books"); setRows(data); }
      catch { toast.error("Failed to load books"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>Loading…</p>;
  // Show "looking_for_publisher" books first
  const sorted = [...rows].sort((a, b) => Number(b.looking_for_publisher) - Number(a.looking_for_publisher));
  return (
    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="publisher-browse-books">
      {sorted.map((b) => (
        <li key={b.book_id} className="p-4 rounded-lg" style={{ background: "white", border: "1px solid var(--bf-border)" }} data-testid={`pub-book-${b.book_id}`}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="font-serif text-lg leading-tight">{b.title}</p>
            {b.looking_for_publisher && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: "#FFEDD5", color: "#9A3412" }}>Open to publisher</span>
            )}
          </div>
          <p className="text-xs mb-1" style={{ color: "var(--bf-text-muted)" }}>By {b.author?.name || "Unknown"}</p>
          <p className="text-xs mb-2" style={{ color: "var(--bf-text-muted)" }}>
            {(b.genres || []).join(", ") || "—"} · {(b.language || "en").toUpperCase()}
          </p>
          {b.description && <p className="text-sm line-clamp-3">{b.description}</p>}
        </li>
      ))}
    </ul>
  );
}

export default function PublisherDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/publisher/profile"); setProfile(data); }
      catch { toast.error("Failed to load profile"); }
    })();
  }, []);

  if (!profile) return <div className="min-h-screen flex items-center justify-center text-sm" data-testid="publisher-loading">Loading…</div>;

  return (
    <div className="min-h-screen" style={{ background: "var(--bf-bg)" }} data-testid="publisher-dashboard">
      <SiteNav />
      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-14">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="w-7 h-7" style={{ color: "var(--bf-primary)" }} />
          <div className="label-eyebrow">Publisher console</div>
        </div>
        <h1 className="font-serif text-5xl mb-1">{profile.company_name || user?.name}</h1>
        <p className="text-base mb-6" style={{ color: "var(--bf-text-muted)" }}>
          Manage your listing, browse open manuscripts, and respond to author proposals.
        </p>

        <StatusBanner profile={profile} />

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="bg-transparent border-b w-full justify-start gap-8 rounded-none p-0 mb-8" style={{ borderColor: "var(--bf-border)" }}>
            <TabsTrigger value="profile" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="pub-tab-profile">
              <Building2 className="w-4 h-4 mr-2" /> Profile &amp; pricing
            </TabsTrigger>
            <TabsTrigger value="proposals" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="pub-tab-proposals">
              <ClipboardCheck className="w-4 h-4 mr-2" /> Proposals
            </TabsTrigger>
            <TabsTrigger value="browse" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="pub-tab-browse">
              <BookOpen className="w-4 h-4 mr-2" /> Browse books
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-0">
            <ProfileForm profile={profile} onSaved={setProfile} />
          </TabsContent>
          <TabsContent value="proposals" className="mt-0">
            <ProposalsList />
          </TabsContent>
          <TabsContent value="browse" className="mt-0">
            <BrowseBooks />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
